import { useEffect, useState } from "react";
import v2DashboardApi from "../services/v2DashboardApi";
import { ensureAccountId } from "../session";
import { hasTeacherSimsPurchase } from "./useTeacherSimsAccount";

const TOP_N = 3;

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.entries)) return value.entries;
  if (Array.isArray(value?.sims)) return value.sims;
  if (Array.isArray(value?.topSims)) return value.topSims;
  if (Array.isArray(value?.clientSims)) return value.clientSims;
  if (Array.isArray(value?.accountTop3)) return value.accountTop3;
  if (Array.isArray(value?.globalTop3)) return value.globalTop3;
  return [];
};

const unwrapBody = (payload) => {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }
  const nested = payload.data;
  if (
    nested &&
    typeof nested === "object" &&
    !Array.isArray(nested) &&
    (nested.accountTop3 ||
      nested.globalTop3 ||
      nested.simUsedCount != null ||
      nested.purchaseTypes)
  ) {
    return nested;
  }
  return payload;
};

const isCombinedSnapshot = (data) =>
  Boolean(
    data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      (Array.isArray(data.accountTop3) ||
        Array.isArray(data.globalTop3) ||
        (data.simUsedCount != null && Array.isArray(data.purchaseTypes)))
  );

const toDashboardSnapshot = (
  purchaseTypesPayload,
  countPayload,
  accountTop3Payload,
  globalTop3Payload
) => {
  const combined = [
    purchaseTypesPayload,
    countPayload,
    accountTop3Payload,
    globalTop3Payload,
  ]
    .map(unwrapBody)
    .find(isCombinedSnapshot);

  if (combined) {
    return {
      clientSims: normalizeNamedPercentList(combined.accountTop3),
      globalSims: normalizeNamedPercentList(combined.globalTop3),
      totalCount: parseSimUsedCount(combined.simUsedCount ?? combined),
      showOnlyProfessionalStandardsReport: !hasTeacherSimsPurchase(
        combined.purchaseTypes ?? combined
      ),
    };
  }

  return {
    clientSims: normalizeNamedPercentList(accountTop3Payload),
    globalSims: normalizeNamedPercentList(globalTop3Payload),
    totalCount: parseSimUsedCount(countPayload),
    showOnlyProfessionalStandardsReport: !hasTeacherSimsPurchase(
      purchaseTypesPayload
    ),
  };
};

const unwrapPayload = (payload) => {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  return payload.data ?? payload;
};

const simLabel = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return item.simName || item.name || item.label || item.simulationName || "";
};

const asTotalCount = (value, fallback) => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
};

const parseSimUsedCount = (payload, depth = 0) => {
  if (payload == null || depth > 4) return 0;
  if (typeof payload === "number" || typeof payload === "string") {
    return asTotalCount(payload, 0);
  }
  if (typeof payload !== "object") return 0;

  const direct = asTotalCount(
    payload.count ??
      payload.simUsedCount ??
      payload.totalCount ??
      payload.value ??
      payload.simsUsed ??
      payload.usedCount,
    null
  );
  if (direct != null) return direct;

  const nested = payload.data ?? payload.body ?? payload.result;
  if (nested != null && nested !== payload) {
    return parseSimUsedCount(nested, depth + 1);
  }
  return 0;
};

const clampPercent = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
};

const simPercentage = (item) => {
  if (item == null || typeof item === "string") return 0;
  return clampPercent(item.percentage ?? item.Percentage ?? item.value ?? 0);
};

const normalizeNamedPercentList = (payload) =>
  asList(unwrapPayload(payload))
    .map((item) => ({
      label: simLabel(item),
      value: simPercentage(item),
    }))
    .filter((item) => item.label)
    .sort((a, b) => b.value - a.value)
    .slice(0, TOP_N);

const EMPTY_SNAPSHOT = {
  clientSims: [],
  globalSims: [],
  totalCount: 0,
  showOnlyProfessionalStandardsReport: true,
};

export const useSimUsedCount = () => {
  const [simUsedCount, setSimUsedCount] = useState(EMPTY_SNAPSHOT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const accountId = await ensureAccountId();
        if (cancelled) return;
        if (accountId == null) {
          setSimUsedCount(EMPTY_SNAPSHOT);
          return;
        }

        const [
          purchaseTypesPayload,
          countPayload,
          accountTop3Payload,
          globalTop3Payload,
        ] = await Promise.all([
          v2DashboardApi().getPurchaseTypes(accountId).catch(() => []),
          v2DashboardApi().getSimUsedCount(accountId).catch(() => 0),
          v2DashboardApi().getTop3ByAccount(accountId).catch(() => []),
          v2DashboardApi().getGlobalTop3().catch(() => []),
        ]);

        if (cancelled) return;

        setSimUsedCount(
          toDashboardSnapshot(
            purchaseTypesPayload,
            countPayload,
            accountTop3Payload,
            globalTop3Payload
          )
        );
      } catch {
        if (!cancelled) setSimUsedCount(EMPTY_SNAPSHOT);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { simUsedCount, loading };
};
